import * as RadixHoverCard from '@radix-ui/react-hover-card';
import { formatPercent, preciseCurrencyFormatter } from '@shared/helpers/format-currency';
import { addMonths } from 'date-fns/addMonths';
import React, { type ReactNode } from 'react';
import { bestValueTierIndex, type Product } from '../../../../data/product';
import type { VolumeDiscount } from '../../../../data/product-pricing';
import { formatCases } from '../../../../helpers/format-quantity';
import { longMonthName } from '../../../../helpers/months';
import type { ProductOrderItem } from '../data/product-order-item';

/**
 * Pixel width of one deal column. The next-month popover renders one cell
 * per slot at this width and skids horizontally by `(slot-1) * DEAL_COLUMN_PX`
 * to overlay the row's deal cells, so any host table that wants the popover
 * to line up MUST pin its deal columns to this same width.
 */
export const DEAL_COLUMN_PX = 80;

function getDeal(product: Product, period: 'previous' | 'current' | 'future', slot: number) {
  if (period === 'current') {
    if (!product.pricing.current_cost) {
      return;
    }

    const discounts = product.pricing.volume_discounts;
    if (discounts === undefined || discounts.length <= slot - 1) {
      return;
    }

    return {
      frontlineCost: product.pricing.current_cost,
      discount: discounts[slot - 1],
    };
  }
}

/**
 * Generic four-row right-aligned grid used by compact stacked cells.
 * Any row left `undefined` renders a non-breaking space so spacing stays
 * consistent across the table.
 */
export function FourCellGrid({
  row1,
  row2,
  row3,
  row4,
}: {
  row1?: ReactNode;
  row2?: ReactNode;
  row3?: ReactNode;
  row4?: ReactNode;
}) {
  return (
    <div className="grid grid-rows-4 justify-items-end">
      <div className="flex h-6 items-center p-px">{row1 ?? ' '}</div>
      <div className="flex h-6 items-center p-px">{row2 ?? ' '}</div>
      <div className="flex h-6 items-center p-px">{row3 ?? ' '}</div>
      <div className="flex h-6 items-center p-px">{row4 ?? ' '}</div>
    </div>
  );
}

/**
 * Semantic wrapper for deal-specific stacks. Slot order is fixed so cells
 * line up vertically: min qty / cost / dollar savings / percent savings.
 */
export function DealCellGrid({
  minQty,
  cost,
  dollarSavings,
  percentSavings,
}: {
  minQty?: ReactNode;
  cost?: ReactNode;
  dollarSavings?: ReactNode;
  percentSavings?: ReactNode;
}) {
  return <FourCellGrid row1={minQty} row2={cost} row3={dollarSavings} row4={percentSavings} />;
}

type VolumeDiscountCellProps = {
  orderItem: ProductOrderItem;
  slot: number;
};

/**
 * Renders one deal-tier cell for an order item. Exported so other tables
 * (e.g. the family SKU table) can drop it in without going through a fake
 * TanStack `cell` invocation.
 */
export function VolumeDiscountCell({ orderItem, slot }: VolumeDiscountCellProps) {
  const deal = getDeal(orderItem.product, 'current', slot);
  if (!deal) {
    return null;
  }

  const { frontlineCost, discount } = deal;
  const tiers = orderItem.product.pricing.volume_discounts ?? [];
  const isBestValue = tiers.length > 1 && slot - 1 === bestValueTierIndex(tiers, frontlineCost);

  // The next-month popover renders the deal grid with its own chrome and skids
  // horizontally to overlay the row's deal cells, so the hover-card host stays
  // transparent and collision avoidance is disabled — any flip/shift would
  // break the column-for-column alignment.
  return (
    <RadixHoverCard.Root openDelay={0} closeDelay={0}>
      <RadixHoverCard.Trigger asChild>
        <VolumeDiscountDisplay
          frontlineCost={frontlineCost}
          discount={discount}
          isBestValue={isBestValue}
        />
      </RadixHoverCard.Trigger>
      <RadixHoverCard.Portal>
        <RadixHoverCard.Content
          side="bottom"
          align="start"
          sideOffset={10}
          alignOffset={-(slot - 1) * DEAL_COLUMN_PX - 5}
          avoidCollisions={false}
          className="z-popover"
        >
          <NextMonthDiscounts product={orderItem.product} />
        </RadixHoverCard.Content>
      </RadixHoverCard.Portal>
    </RadixHoverCard.Root>
  );
}

const VolumeDiscountDisplay = React.forwardRef<
  HTMLDivElement,
  {
    frontlineCost: number;
    discount: VolumeDiscount;
    isBestValue?: boolean;
  }
>(function VolumeDiscountDisplay({ frontlineCost, discount, isBestValue, ...props }, ref) {
  const dollarSavings = frontlineCost - discount.cost;
  const percentSavings = frontlineCost > 0 ? dollarSavings / frontlineCost : 0;

  return (
    <div {...props} ref={ref}>
      <DealCellGrid
        minQty={
          <div className="flex items-center gap-0.5 p-px">
            {isBestValue && <span className="text-[12px] leading-none text-amber-500">★</span>}
            {formatCases(discount.min_cases)}
          </div>
        }
        cost={preciseCurrencyFormatter(discount.cost)}
        dollarSavings={
          <span className={SAVINGS_CLASS}>{preciseCurrencyFormatter(dollarSavings)}</span>
        }
        percentSavings={<span className={SAVINGS_CLASS}>{formatPercent(percentSavings)}</span>}
      />
    </div>
  );
});

const SAVINGS_CLASS = 'text-emerald-600 font-semibold';

function NextMonthDiscounts({ product }: { product: Product }) {
  if (!product.pricing.future_volume_discounts) {
    return;
  }

  if (product.pricing.future_cost === undefined) {
    return;
  }

  const nextMonthName = longMonthName(addMonths(new Date(), 1));

  return (
    <div className="flex flex-col bg-white rounded-sm shadow-xl border border-gray-200">
      <div className="grid grid-flow-col divide-x divide-gray-200 font-mono">
        {product.pricing.future_volume_discounts.map((discount, slot) => {
          const sameAsCurrent = product.pricing.volume_discounts?.[slot]?.cost === discount.cost;

          return (
            <div
              key={`${discount.min_cases}-${discount.cost}`}
              className={`grid px-1 py-1 text-xs text-gray-700 truncate text-clip content-center ${sameAsCurrent ? 'opacity-60' : ''}`}
              style={{ width: DEAL_COLUMN_PX }}
            >
              <VolumeDiscountDisplay
                frontlineCost={product.pricing.future_cost as number}
                discount={discount}
              />
            </div>
          );
        })}
      </div>
      <h3 className="text-xs p-1 text-center border-t border-gray-200">{nextMonthName}</h3>
    </div>
  );
}
